import { Stack } from '@chakra-ui/react';
import { Surcharge, SurchargeUnit } from '@pelicargo/types';
import {
  allCargoTypeOptions,
  costUnitTypeOptions,
  freightServiceOptions,
  serviceTypeOptions,
  surchargeCategoryOptions,
  surchargeUnitOptions,
  surchargeWeightTypeOptions,
} from '@pelicargo/utils';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { trpc } from '../../config/trpc';
import { SurchargeActionState } from '../../utils/types';
import { AirlineInput } from '../Autocomplete/AirlineInput';
import { AirportInput } from '../Autocomplete/AirportInput';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleInput } from '../SimpleInput';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleTextarea } from '../SimpleTextarea';

type Props = {
  action: SurchargeActionState;
};

const getEnabledKeys = <T,>(config: { [key in keyof T]: boolean }) => {
  return Object.entries(config)
    .filter(([, value]) => !!value)
    .map(([key]) => key as keyof T);
};

const EXPIRE_FIELDS: string[] = getEnabledKeys<Partial<Surcharge>>({
  expires_at: true,
});
const REPLACE_FIELDS: string[] = getEnabledKeys<Partial<Surcharge>>({
  starts_at: true,
  expires_at: true,
  min_cost: true,
  per_unit_cost: true,
  account_id: true,
});

export const SurchargeForm = ({ action }: Props) => {
  const { data: allAccounts } = trpc.getAllAccounts.useQuery();

  // Checks to see if field is not in the list of enabled keys for that form.
  const isFieldDisabled = useCallback(
    (fieldName: string) => {
      if (action === 'expire') return !EXPIRE_FIELDS.includes(fieldName);
      if (action === 'replace') return !REPLACE_FIELDS.includes(fieldName);
      return false;
    },
    [action],
  );

  const isUnitKG = useWatch({ name: 'unit' }) === SurchargeUnit.KG;

  return (
    <>
      <Stack direction="column" spacing={4} w="full">
        <SimpleInput
          name="airline_label"
          label="Airline Label"
          placeholder="Airline Label"
          isDisabled={isFieldDisabled('airline_label')}
          isRequired
        />
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <AirlineInput
            label="Airline"
            name="provider_id"
            placeholder="Airline"
            isDisabled={isFieldDisabled('provider_id')}
          />
          <SimpleSelect
            name="surcharge_category"
            label="Category"
            isDisabled={isFieldDisabled('surcharge_category')}
            isRequired
            options={surchargeCategoryOptions}
          />
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <SimpleLabeledPopoverInput
            name="starts_at"
            label="Date valid from"
            placeholder="Date valid from"
            isDisabled={isFieldDisabled('starts_at')}
            inputType="date-picker"
            initialTimeZone="Etc/UTC"
            isRequired
            w="full"
            showClearButton
          />
          <SimpleSelect
            name="unit"
            label="Unit type"
            isDisabled={isFieldDisabled('unit')}
            isRequired
            options={surchargeUnitOptions}
          />
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <SimpleLabeledPopoverInput
            name="expires_at"
            label="Date valid to"
            placeholder="Date valid to"
            isDisabled={isFieldDisabled('expires_at')}
            inputType="date-picker"
            initialTimeZone="Etc/UTC"
            w="full"
            showClearButton
          />
          <SimpleSelect
            name="weight_type"
            label="Weight type"
            placeholder=""
            isDisabled={isFieldDisabled('weight_type')}
            isRequired={isUnitKG}
            options={surchargeWeightTypeOptions}
          />
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <SimpleInput
            name="airline_code"
            label="Airline code"
            placeholder="Airline code"
            isDisabled={isFieldDisabled('airline_code')}
          />
          <SimpleInput
            name="airline_product"
            label="Airline product"
            placeholder="Airline product"
            isDisabled={isFieldDisabled('airline_product')}
          />
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <SimpleSelect
            name="freight_service"
            label="Freight Service"
            isDisabled={isFieldDisabled('freight_service')}
            options={freightServiceOptions}
          />
          <SimpleSelect
            name="service_type"
            label="Service Type"
            isDisabled={isFieldDisabled('service_type')}
            options={serviceTypeOptions}
          />
          <SimpleSelect
            name="cargo_type"
            label="Cargo Type"
            isDisabled={isFieldDisabled('cargo_type')}
            options={allCargoTypeOptions}
          />
        </Stack>
        <Stack direction="row">
          <AirportInput
            name="origin_airports"
            label="Origins"
            isDisabled={isFieldDisabled('origin_airports')}
            placeholder="YVR, YYJ, YYZ"
            isMulti
          />
          <AirportInput
            name="destination_airports"
            label="Destinations"
            isDisabled={isFieldDisabled('destination_airports')}
            placeholder="YVR, YYJ, YYZ"
            isMulti
          />
        </Stack>
        <Stack direction={{ base: 'column', md: 'row' }} w="full">
          <SimpleInput
            name="min_cost"
            label="Minimum cost"
            placeholder="Minimum cost"
            type="number"
            isDisabled={isFieldDisabled('min_cost')}
            flex="1"
          />
          <Stack direction="row" flex="1">
            <SimpleSelect
              name="per_unit_cost_type"
              label="Unit"
              placeholder=""
              isDisabled={isFieldDisabled('per_unit_cost_type')}
              options={costUnitTypeOptions}
            />
            <SimpleInput
              name="per_unit_cost"
              label="Per unit cost"
              type="number"
              placeholder="Per unit cost"
              isDisabled={isFieldDisabled('per_unit_cost')}
            />
          </Stack>
        </Stack>
        <SimpleTextarea
          name="comments"
          label="Comments"
          placeholder="Comments"
          isDisabled={isFieldDisabled('comments')}
        />
        <SimpleCheckbox name="is_optional" label="Optional" isDisabled={isFieldDisabled('is_optional')} />

        <SimpleReactSelect
          name="account_id"
          label="Parent Account"
          isSearchable
          isMulti={false}
          options={allAccounts?.map((account) => ({
            label: account?.name,
            value: account?.id,
          }))}
          isDisabled={isFieldDisabled('account_id')}
        />
      </Stack>
    </>
  );
};
